import React, { ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { SearchContext } from '@context/search.context';
import { useContextSelector } from 'use-context-selector';
import useSearch from '@hooks/orders-sales/use-search';
import Icon from '@components/atoms/icon/icon';
import { RESULT_PER_PAGE } from '@constants/orders-sales';
import styles from '../filters.module.scss';
import SearchModal from './search-modal';

export const DEFAULT_FIRST_SEARCH_PAGE = 1;

interface SearchProps {
  currentPage: number;
  onProductIdChange: (ids: number[]) => void;
  setMoreSearch: (v: boolean) => void;
  setSubmittedSearchText: (v: string) => void;
  setSearchFetching: (v: boolean) => void;
  setSearchEmpty: (v: boolean) => void;
}

const Search: React.FC<SearchProps> = ({
  currentPage,
  onProductIdChange,
  setMoreSearch,
  setSubmittedSearchText,
  setSearchFetching,
  setSearchEmpty,
}) => {
  const { t } = useTranslation(['default-layout', 'orders-sales']);

  const searchQuery = useContextSelector(SearchContext, (v) => v.searchQuery);
  const isSearchHeaderOpen = useContextSelector(SearchContext, (v) => v.isSearchHeaderOpen);
  const setIsSearchHeaderOpen = useContextSelector(SearchContext, (v) => v.setIsSearchHeaderOpen);
  const setSearchQuery = useContextSelector(SearchContext, (v) => v.setSearchQuery);

  const { fetchSearch } = useSearch();

  const handleFetchSearch = async (text: string) => {
    setSearchEmpty(false);

    const result = await fetchSearch(text, currentPage);

    if (result && result.items?.length) {
      const productIds = result.items.map((i) => i.id);
      onProductIdChange(productIds);
    } else {
      /* Searching internal product id */
      if (/^\d{3,10}$/.test(text)) {
        const id = [Number(text)];
        onProductIdChange(id);
      } else {
        setSearchEmpty(true);
      }
    }

    if (result && currentPage * RESULT_PER_PAGE < result.paginationStats.totalHits) {
      setMoreSearch(true);
    } else {
      setMoreSearch(false);
    }
  };

  useEffect(() => {
    if (currentPage > 1) {
      handleFetchSearch(searchQuery);
    }
  }, [currentPage]);

  const handleSubmit = async (text?: string) => {
    const searchValue = text || searchQuery;

    setSubmittedSearchText(searchValue);
    setIsSearchHeaderOpen(false);

    if (searchValue) {
      setSearchFetching(true);
      handleFetchSearch(searchValue).finally(() => {
        setSearchFetching(false);
      });
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (!isSearchHeaderOpen) {
      setIsSearchHeaderOpen(true);
    }
  };

  return (
    <div className={styles.search}>
      <div className={styles.searchButton}>
        <Icon
          className={styles.searchButton_icon}
          name="search"
          ariaHidden
        />

        <input
          autoComplete="off"
          placeholder={t('orders-sales:MY_ACCOUNT.ORDERS_SALES.LIST.SEARCH.DESC')}
          id="sales_searchbar__input"
          className={styles.searchButton_input}
          type="text"
          onChange={onChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmit();
            }
          }}
          data-cy="search_input"
          value={searchQuery}
        />

        {!!searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className={styles.searchButton_clearButton}
            data-cy="clear_button"
          >
            {t('HEADER.SEARCH.CLEAR_BTN')}
          </button>
        )}
      </div>

      {isSearchHeaderOpen && !!searchQuery && (
        <SearchModal
          onSubmit={handleSubmit}
          setIsSearchHeaderOpen={setIsSearchHeaderOpen}
        />
      )}
    </div>
  );
};

export default Search;
